/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Rollback-attack defence for the vault envelope.
 *
 * The sync server stores the envelope as an opaque blob and so cannot
 * detect when it serves an *older* valid envelope — reverting a key
 * rotation, reviving a retired key, restoring a deleted contact. The
 * client tracks the highest-seen counter per envelopeId in localStorage
 * (or any `RollbackTracker` injected for tests). On unlock, after
 * successful AEAD verification, the AAD-bound counter is compared to the
 * stored value:
 *
 *   counter <  stored  → RollbackDetectedError (server replayed)
 *   counter == stored  → accept (re-open of same envelope)
 *   counter >  stored  → accept and persist new high-water mark
 *
 * Detection happens *after* the AEAD tags verify, so this error class is
 * intentionally distinguishable from `EnvelopeOpenError` — by the time
 * we get here, we know the bytes are authentic; the user genuinely needs
 * to know it's rollback (not tamper, not wrong passphrase) so the UI can
 * surface a server-compromise warning rather than a generic "wrong key".
 */

const STORAGE_KEY_PREFIX = 'td.vault.counter.v1.';

/**
 * Key used at construction time to prove `localStorage` is writable.
 *
 * #120: the previous form `${STORAGE_KEY_PREFIX}__probe__` collided with
 * `storageKeyFor('__probe__')`, so constructing a tracker silently
 * `removeItem`-ed the high-water mark for an envelope with
 * `envelopeId === '__probe__'`. Real envelopeIds are base64nopad slices
 * of SHA-256 output (per `vault.ts:395`), so the collision is
 * astronomically unlikely in production — but it's exactly the kind
 * of "probably impossible" footgun that breaks one specific test seed
 * years from now.
 *
 * The `#` separator is unambiguously outside the base64nopad alphabet
 * (`[A-Za-z0-9+/]`), so no real envelopeId can ever produce this key.
 * Staying inside `STORAGE_KEY_PREFIX` keeps the key sweepable by any
 * test cleanup that does `key.startsWith(STORAGE_KEY_PREFIX)`.
 */
const PROBE_STORAGE_KEY = `${STORAGE_KEY_PREFIX}#probe`;

/**
 * Thrown when the envelope's AAD-bound counter is older than the highest
 * counter previously seen for the same envelopeId, OR when the tracker's
 * storage backend fails (no rollback protection without a working store).
 */
export class RollbackDetectedError extends Error {
  /** The counter on the envelope just decrypted. */
  readonly observedCounter: number;
  /** The previously-stored high-water mark, or `null` if storage failed. */
  readonly storedCounter: number | null;

  constructor(
    message: string,
    observedCounter: number,
    storedCounter: number | null
  ) {
    super(message);
    this.name = 'RollbackDetectedError';
    this.observedCounter = observedCounter;
    this.storedCounter = storedCounter;
  }
}

/**
 * Storage abstraction so tests can inject in-memory backings and the
 * decrypt path doesn't have to know about localStorage.
 */
export interface RollbackTracker {
  /** Highest counter previously seen for this envelopeId, or `null` if never seen. */
  getHighest(envelopeId: string): number | null;
  /** Persist a new high-water mark for this envelopeId. */
  setHighest(envelopeId: string, counter: number): void;
}

const storageKeyFor = (envelopeId: string): string =>
  `${STORAGE_KEY_PREFIX}${envelopeId}`;

/**
 * `RollbackTracker` backed by `localStorage`. Throws if
 * `localStorage` is unavailable on construction (private-mode browsing,
 * sandboxed iframes, etc.) — the caller can decide whether to proceed
 * without rollback protection or to refuse to open the vault.
 */
export const localStorageTracker = (): RollbackTracker => {
  // Eagerly probe localStorage so the failure surfaces at construction
  // rather than at first read. The probe key sits in a slot that no
  // real envelopeId can ever land on (see PROBE_STORAGE_KEY).
  try {
    localStorage.setItem(PROBE_STORAGE_KEY, '1');
    localStorage.removeItem(PROBE_STORAGE_KEY);
  } catch (cause) {
    throw new Error('localStorage is unavailable for rollback tracking', {
      cause,
    });
  }

  return {
    getHighest(envelopeId: string): number | null {
      const raw = localStorage.getItem(storageKeyFor(envelopeId));
      if (raw === null) return null;
      const n = Number(raw);
      // Reject malformed values defensively — treat as no entry.
      if (!Number.isInteger(n) || n < 0) return null;
      return n;
    },
    setHighest(envelopeId: string, counter: number): void {
      localStorage.setItem(storageKeyFor(envelopeId), String(counter));
    },
  };
};

/**
 * In-memory `RollbackTracker` backed by a `Map`. For tests and any
 * caller that wants ephemeral protection within a single process.
 */
export const inMemoryTracker = (): RollbackTracker => {
  const store = new Map<string, number>();
  return {
    getHighest(envelopeId: string): number | null {
      return store.has(envelopeId) ? (store.get(envelopeId) ?? null) : null;
    },
    setHighest(envelopeId: string, counter: number): void {
      store.set(envelopeId, counter);
    },
  };
};

/**
 * Verify the envelope's counter against the tracker and persist the new
 * high-water mark on success.
 *
 * Behaviour:
 *  - tracker storage fails → `RollbackDetectedError` (no protection ⇒ refuse)
 *  - observed < stored      → `RollbackDetectedError` (replay)
 *  - observed == stored     → no-op (re-open)
 *  - observed > stored      → persist new value
 *  - stored is null         → first sight; persist
 *
 * @param envelopeId - the envelope's identifier (from outer header)
 * @param observedCounter - the AAD-bound counter from the just-decrypted envelope
 * @param tracker - storage layer
 * @throws {RollbackDetectedError}
 */
export const checkAndPersist = (
  envelopeId: string,
  observedCounter: number,
  tracker: RollbackTracker
): void => {
  let stored: number | null;
  try {
    stored = tracker.getHighest(envelopeId);
  } catch {
    throw new RollbackDetectedError(
      'rollback tracker storage unavailable',
      observedCounter,
      null
    );
  }

  if (stored !== null && observedCounter < stored) {
    throw new RollbackDetectedError(
      `envelope counter ${observedCounter} is older than stored ${stored}`,
      observedCounter,
      stored
    );
  }

  if (stored === null || observedCounter > stored) {
    try {
      tracker.setHighest(envelopeId, observedCounter);
    } catch {
      throw new RollbackDetectedError(
        'rollback tracker write failed',
        observedCounter,
        stored
      );
    }
  }
};

/**
 * Record a save event after the server confirms write. Called by the
 * higher-level vault save path once F9 (#49) wires it up.
 *
 * @param envelopeId - the envelope just persisted
 * @param savedCounter - the counter value committed to the server
 * @param tracker - storage layer to update
 */
export const recordSave = (
  envelopeId: string,
  savedCounter: number,
  tracker: RollbackTracker
): void => {
  tracker.setHighest(envelopeId, savedCounter);
};

/** Exposed for tests that want to inspect the storage key format. */
export const __testing = {
  STORAGE_KEY_PREFIX,
  storageKeyFor,
  PROBE_STORAGE_KEY,
};
